import { useEffect, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";

/** The event Chromium browsers fire when the app can be installed; not in the DOM typings. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * "Install" in the app bar, shown only when the browser says the app can be installed and it is not
 * already running installed. Nothing to show on browsers that do not support it — iOS Safari installs
 * through its own Share → Add to Home Screen, which the page cannot trigger.
 */
export function InstallPrompt() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches;
    if (standalone) return;

    const onPrompt = (event: Event) => {
      // Keep the browser's own mini-infobar out of the way; the button offers it instead.
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setPrompt(null);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!prompt) return null;

  return (
    <Tooltip title="Add HealNTrack HMS to your home screen">
      <Button
        size="small"
        startIcon={<InstallMobileIcon />}
        onClick={async () => {
          await prompt.prompt();
          await prompt.userChoice;
          setPrompt(null);
        }}
      >
        Install
      </Button>
    </Tooltip>
  );
}
